/**
 * EquipScopeContext
 *
 * Shared context for EQuIP scope selector state (managers + clinicians).
 * Loaded at the layout level on EQuIP routes. The header renders the
 * appropriate dropdown based on the current route, and page components
 * consume the selected values.
 */

import { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import {
  useEquipManagers,
  useEquipClinicians,
  useEquipSupervisingPhysicians,
  useEquipSelfIsProvider,
  useAvailableSeniorLeads,
  type SeniorLeadOption,
} from '@/hooks/equip/useEquipScope'
import type { ManagerOption, ClinicianOption, SupervisingPhysicianOption } from '@/lib/equip-types'
import { useCurrentQuarter } from '@/hooks/equip/useCurrentQuarter'

interface EquipScopeContextValue {
  // Manager scope (Leadership page, admin + senior lead)
  managers: ManagerOption[]
  selectedManager: string | null
  setSelectedManager: (email: string | null) => void
  isLoadingManagers: boolean

  // Senior lead scope (Senior Leadership page, admin only)
  seniorLeads: SeniorLeadOption[]
  selectedSeniorLead: string | null
  setSelectedSeniorLead: (email: string | null) => void
  isLoadingSeniorLeads: boolean

  // Clinician scope (Clinician page, admin/leadership)
  clinicians: ClinicianOption[]
  selectedClinician: string | null
  setSelectedClinician: (email: string | null) => void
  isLoadingClinicians: boolean

  // Supervising physician scope (APC Dashboard, admin only)
  supervisingPhysicians: SupervisingPhysicianOption[]
  selectedPhysicianNpi: number | null
  setSelectedPhysicianNpi: (npi: number | null) => void
  isLoadingSupervisingPhysicians: boolean

  // Quarter state (for snapshot banner)
  selectedYear: number
  selectedQuarter: number
  setQuarter: (year: number, quarter: number) => void
  isHistoricalQuarter: boolean

  // Role flags
  isAdmin: boolean
  isSeniorLead: boolean
  showClinicianDropdown: boolean
  /** True if the leadership user also has their own EQuIP performance data (GLR assignment). */
  selfIsProvider: boolean
}

const EquipScopeContext = createContext<EquipScopeContextValue | null>(null)

export function useEquipScope() {
  const ctx = useContext(EquipScopeContext)
  if (!ctx) throw new Error('useEquipScope must be used within EquipScopeProvider')
  return ctx
}

/** Optional hook — returns null outside provider instead of throwing */
export function useEquipScopeOptional() {
  return useContext(EquipScopeContext)
}

interface EquipScopeProviderProps {
  children: ReactNode
  isAdmin: boolean
  isSeniorLead?: boolean
  showClinicianDropdown: boolean
}

export function EquipScopeProvider({ children, isAdmin, isSeniorLead = false, showClinicianDropdown }: EquipScopeProviderProps) {
  // Manager list (admin + senior lead, for Leadership page)
  const managersQuery = useEquipManagers(isAdmin || isSeniorLead)
  const [selectedManager, setSelectedManager] = useState<string | null>(null)

  // Senior lead list (admin only, for Senior Leadership page RDCOE picker)
  const seniorLeadsQuery = useAvailableSeniorLeads(isAdmin)
  const [selectedSeniorLead, setSelectedSeniorLead] = useState<string | null>(null)

  // Clinician list (admin + leadership, for Clinician page)
  const cliniciansQuery = useEquipClinicians(showClinicianDropdown)
  const [selectedClinician, setSelectedClinician] = useState<string | null>(null)

  // Self-provider check: leadership users who also have a GLR assignment
  // default to showing their own data instead of the selection prompt.
  const selfIsProviderQuery = useEquipSelfIsProvider(showClinicianDropdown)

  // Supervising physician list (admin only, for APC Dashboard impersonation)
  const supervisingPhysiciansQuery = useEquipSupervisingPhysicians(isAdmin)
  const [selectedPhysicianNpi, setSelectedPhysicianNpi] = useState<number | null>(null)

  // Quarter state (shared across EQuIP views for snapshot banner). Only fetch
  // when this provider actually has EQuIP scope to show — KeystoneLayout zeroes
  // out isAdmin/isSeniorLead/showClinicianDropdown for users without EQuIP
  // access, so this mirrors its needsEquipScope gate without needing a new prop.
  const hasEquipScope = isAdmin || isSeniorLead || showClinicianDropdown
  const current = useCurrentQuarter(hasEquipScope)
  const [selectedYear, setSelectedYear] = useState<number | null>(null)
  const [selectedQuarter, setSelectedQuarter] = useState<number | null>(null)

  // Resolved values: use selected if set, otherwise use fetched current
  const year = selectedYear ?? current.year
  const quarter = selectedQuarter ?? current.quarter

  const setQuarter = useCallback((y: number, q: number) => {
    setSelectedYear(y)
    setSelectedQuarter(q)
  }, [])
  const isHistoricalQuarter = current.year > 0 && (year !== current.year || quarter !== current.quarter)

  if (current.isLoading) {
    return null
  }

  return (
    <EquipScopeContext.Provider
      value={{
        managers: managersQuery.data ?? [],
        selectedManager,
        setSelectedManager,
        isLoadingManagers: managersQuery.isPending,
        seniorLeads: seniorLeadsQuery.data ?? [],
        selectedSeniorLead,
        setSelectedSeniorLead,
        isLoadingSeniorLeads: seniorLeadsQuery.isPending,
        clinicians: cliniciansQuery.data ?? [],
        selectedClinician,
        setSelectedClinician,
        isLoadingClinicians: cliniciansQuery.isPending,
        supervisingPhysicians: supervisingPhysiciansQuery.data ?? [],
        selectedPhysicianNpi,
        setSelectedPhysicianNpi,
        isLoadingSupervisingPhysicians: supervisingPhysiciansQuery.isPending,
        selectedYear: year,
        selectedQuarter: quarter,
        setQuarter,
        isHistoricalQuarter,
        isAdmin,
        isSeniorLead,
        showClinicianDropdown,
        selfIsProvider: selfIsProviderQuery.data?.is_provider ?? false,
      }}
    >
      {children}
    </EquipScopeContext.Provider>
  )
}
